import type React from 'react';

import { memo } from 'react';
import { Route } from 'react-router';
import { Routes } from '@datadog/browser-rum-react/react-router-v8';

import { PrivateRoutes } from './PrivateRoutes';
import { PublicRoutes } from './PublicRoutes';
import WithSuspense from './WithSuspense';

export type RouteLayout = 'default' | 'split-view';

export type RouteHandle = {
  /**
   * Layout mode for the route.
   * - 'split-view': Adjusts footer styles for split-pane layouts (e.g., table + sidebar).
   *   Used by: SSUM User Properties Assignment page (routes.userPropertiesAssignment)
   *
   * For components that need split-view styles but aren't standalone routes
   * (e.g., steps within a multi-step form), use the useSplitViewLayout hook instead.
   * @see layouts/LayoutContext.tsx
   */
  layout?: RouteLayout;
  /** Shows back button in V2 header when true */
  hasBreadcrumbs?: boolean;
  /**
   * When true, hides the global footer from PageWrapper.
   * Use this when the page needs to render its own footer overriding the default one
   * (e.g., split-view layouts where footer should be in the left column only).
   */
  hideFooter?: boolean;
};

export type RouteParams = {
  path: string;
  component: React.LazyExoticComponent<() => JSX.Element> | React.LazyExoticComponent<React.FC>;
  matchesPath?: (pathname: string) => boolean;
  title?: string;
  role?: 'none' | 'protected';
  redirect?: string;
  handle?: RouteHandle;
};

const createRouteObjects = (routes: RouteParams[]) =>
  routes.map((route: RouteParams) => {
    const Component = WithSuspense(route.component);

    function WrapperComponent() {
      return <Component />;
    }

    return {
      ...route,
      Component: memo(WrapperComponent),
    };
  });

export const publicRoutes = createRouteObjects(PublicRoutes);
export const privateRoutes = createRouteObjects(PrivateRoutes);
export const allRoutes = [...publicRoutes, ...privateRoutes];

function GlobalRouter() {
  return (
    <Routes>
      {allRoutes.map(({ path, Component, handle }) => (
        <Route key={path} path={path} element={<Component />} handle={handle} />
      ))}
    </Routes>
  );
}

export default GlobalRouter;
